import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';

interface StrategyConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentTargetAudience?: string[];
  currentKeyMessages?: string[];
  currentContentGoals?: string[];
  currentBrandVoice?: string[];
  newTargetAudience?: string[];
  newKeyMessages?: string[];
  newContentGoals?: string[];
  newBrandVoice?: string[];
}

const StrategyConfirmationDialog: React.FC<StrategyConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentTargetAudience,
  currentKeyMessages,
  currentContentGoals,
  currentBrandVoice,
  newTargetAudience,
  newKeyMessages,
  newContentGoals,
  newBrandVoice,
}) => {
  const hasCurrentTargetAudience = currentTargetAudience && currentTargetAudience.length > 0;
  const hasCurrentKeyMessages = currentKeyMessages && currentKeyMessages.length > 0;
  const hasCurrentContentGoals = currentContentGoals && currentContentGoals.length > 0;
  const hasCurrentBrandVoice = currentBrandVoice && currentBrandVoice.length > 0;
  const hasNewTargetAudience = newTargetAudience && newTargetAudience.length > 0;
  const hasNewKeyMessages = newKeyMessages && newKeyMessages.length > 0;
  const hasNewContentGoals = newContentGoals && newContentGoals.length > 0;
  const hasNewBrandVoice = newBrandVoice && newBrandVoice.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          '@media (max-width: 600px)': {
            margin: 1,
            maxWidth: 'calc(100% - 16px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Update Existing Content Strategy?
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <DialogContentText sx={{ mb: 3 }}>
          We found existing content strategy information. The following will be updated:
        </DialogContentText>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '50vh',
            overflowY: 'auto',
            '@media (max-width: 600px)': {
              maxHeight: '40vh',
            },
          }}
        >
          {hasCurrentTargetAudience && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Target Audience
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentTargetAudience.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New (will replace current):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewTargetAudience ? newTargetAudience.join(', ') : '(No target audience provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
          {hasCurrentKeyMessages && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Key Messages
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentKeyMessages.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New (will replace current):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewKeyMessages ? newKeyMessages.join(', ') : '(No key messages provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
          {hasCurrentContentGoals && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Content Goals
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentContentGoals.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New (will replace current):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewContentGoals ? newContentGoals.join(', ') : '(No content goals provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
          {hasCurrentBrandVoice && (
            <Alert
              severity="info"
              sx={{
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Brand Voice
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  Current:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  {currentBrandVoice.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  New (will replace current):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {hasNewBrandVoice ? newBrandVoice.join(', ') : '(No brand voice provided)'}
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > button': {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: 120 },
          },
        }}
      >
        <Button onClick={onClose} variant="outlined" fullWidth={false}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" fullWidth={false}>
          Update Strategy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyConfirmationDialog;

